import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import {
  ConfirmationModal,
  Page,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Table,
  TableCell,
} from "@skailer-com/uikit";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { EmploymentTypeEditModal } from "@/pages/settings/employment-types/widgets/EmploymentTypeEditModal.tsx";
import { useEmploymentTypes, useEmploymentTypesDelete } from "@/queries/employment-types.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiEmploymentType } from "@/types/employment-types.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export default function EmploymentTypes() {
  const { t } = useTranslation();
  const modalsService = useModals();

  useTitle(t("core:employment_types"), t("common:settings"));

  const { data: employmentTypes, isLoading, isFetching } = useEmploymentTypes();
  const employmentTypesDelete = useEmploymentTypesDelete();

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.employmentTypes.viewed);
  });

  const handleSave = useCallback((employmentType: ApiEmploymentType | undefined) => {
    modalsService.openModal(EmploymentTypeEditModal, { employmentType: employmentType });
  }, []);

  const handleDelete = useCallback((employmentType: ApiEmploymentType) => {
    modalsService.openModal(ConfirmationModal, {
      question: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await employmentTypesDelete.mutateAsync(employmentType.id);
          analyticsService.trackEvent(analyticEvents.employmentTypes.deleted, {
            [analyticProperties.id]: employmentType.id,
          });
        } catch {
          return;
        }
      },
    });
  }, []);

  const columns = useMemo<ColumnDef<ApiEmploymentType>[]>(
    () => [
      {
        header: t("common:title"),
        cell: (row) => <TableCell>{row.row.original.title}</TableCell>,
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
      {
        header: t("core:employees"),
        cell: (row) => <div className={"text-left px-2"}>{row.row.original.employees_count}</div>,
        accessorKey: "employees_count",
        ...ColumnSizes.status,
      },
    ],
    [employmentTypes]
  );

  const renderRowAction = useCallback(
    (row: Row<ApiEmploymentType>) => (
      <DropdownMenu>
        <DropdownMenuItem
          onClick={() => {
            handleSave(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          title={t("common:edit")}
        />
        <DropdownMenuItem
          onClick={() => {
            handleDelete(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
          title={t("common:delete") + "..."}
        />
      </DropdownMenu>
    ),
    [handleDelete, handleSave, t]
  );

  return (
    <>
      <Page>
        <Page.Header
          showBack={true}
          title={t("core:employment_types")}
          navigateTo={"/settings/employment"}
        >
          <Stack>
            <Button
              onClick={() => handleSave(undefined)}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            >
              {t("common:create_new")}
            </Button>
          </Stack>
        </Page.Header>
        <Page.Content>
          <Table
            renderRowAction={renderRowAction}
            data={employmentTypes || []}
            columns={columns}
            isFetching={isFetching || employmentTypesDelete.isPending}
            showSkeleton={isLoading}
            notFoundComponent={<EmptyStateNothingFound />}
            emptyStateComponent={<EmptyState />}
          />
        </Page.Content>
      </Page>
    </>
  );
}
